/**
 * AdminPiiComplianceWidget — monitoring conformité PII audio.
 *
 * KPIs globaux + liste des récidivistes + alertes actives.
 * Zéro PII brute — n'affiche que des types, compteurs, hashs tronqués.
 *
 * Endpoints :
 *   GET  /api/admin/pii/kpis
 *   GET  /api/admin/pii/offenders?role=client|coach
 *   POST /api/admin/pii/events/:id/false-positive
 *   POST /api/admin/pii/alerts/:id/resolve
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ShieldCheck, ShieldAlert, Mail, Phone, Link as LinkIcon,
  TrendingUp, Loader2, RefreshCw, Users, UserCog,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();
const REFRESH_MS = 60000;

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#F1ECDC] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}20` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-xl font-light text-[#2A2319] tabular-nums">{value}</p>
          <p className="text-xs text-[#6B7280] truncate">{label}</p>
        </div>
      </div>
    </div>
  );
}

function LevelBadge({ level }) {
  const cfg = level >= 2
    ? { bg: 'bg-red-500/15', color: 'text-red-400', border: 'border-red-500/30', label: 'Niveau 2' }
    : { bg: 'bg-amber-500/15', color: 'text-amber-400', border: 'border-amber-500/30', label: 'Niveau 1' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

export default function AdminPiiComplianceWidget() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [kpis, setKpis] = useState(null);
  const [offendersClient, setOffendersClient] = useState([]);
  const [offendersCoach, setOffendersCoach] = useState([]);
  const [tab, setTab] = useState('client');
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [kR, ocR, ogR] = await Promise.all([
        fetch(`${API}/admin/pii/kpis`, { headers }),
        fetch(`${API}/admin/pii/offenders?role=client&limit=20`, { headers }),
        fetch(`${API}/admin/pii/offenders?role=coach&limit=20`, { headers }),
      ]);
      if (kR.ok) setKpis(await kR.json());
      if (ocR.ok) setOffendersClient((await ocR.json()).offenders || []);
      if (ogR.ok) setOffendersCoach((await ogR.json()).offenders || []);
    } catch (e) {
      console.error('[PiiWidget] fetch failed', e);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(iv);
  }, [fetchAll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="pii-compliance-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  const currentList = tab === 'client' ? offendersClient : offendersCoach;

  return (
    <div className="space-y-5" data-testid="pii-compliance-widget">
      {/* Header */}
      <div className="bg-white border border-[#F1ECDC] rounded-xl p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="uppercase text-[10px] tracking-[0.2em] text-[#6B7280]">Conformité RGPD</p>
              <p className="text-[#2A2319] text-sm font-medium">Détection PII audio (zéro coût)</p>
            </div>
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                (kpis?.active_alerts ?? 0) > 0
                  ? 'bg-red-500/10 text-red-400 border-red-500/20'
                  : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              }`}
              data-testid="pii-health-chip"
            >
              {(kpis?.active_alerts ?? 0) > 0 ? `${kpis.active_alerts} alerte${kpis.active_alerts > 1 ? 's' : ''} active${kpis.active_alerts > 1 ? 's' : ''}` : 'Conforme'}
            </span>
          </div>
          <button
            onClick={fetchAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FAF7EE] hover:bg-[#F1ECDC] text-[#374151] text-xs transition"
            data-testid="pii-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Actualiser
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Mail} label="Emails détectés" value={kpis?.by_type?.email ?? 0} color="#60A5FA" testid="pii-kpi-email" />
        <Kpi icon={Phone} label="Téléphones détectés" value={kpis?.by_type?.phone ?? 0} color="#F59E0B" testid="pii-kpi-phone" />
        <Kpi icon={LinkIcon} label="URLs détectées" value={kpis?.by_type?.url ?? 0} color="#A78BFA" testid="pii-kpi-url" />
        <Kpi icon={TrendingUp} label="7 derniers jours" value={kpis?.last_7d_count ?? 0} color="#10B981" testid="pii-kpi-7d" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Kpi icon={ShieldCheck} label="Via live (Web Speech)" value={kpis?.total_live ?? 0} color="#10B981" testid="pii-kpi-live" />
        <Kpi icon={ShieldAlert} label="Via post-session (rattrapage)" value={kpis?.total_post_session ?? 0} color="#F97316" testid="pii-kpi-post" />
      </div>

      {/* Offenders */}
      <div className="bg-white border border-[#F1ECDC] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#F1ECDC] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-400" />
            <h3 className="text-[#2A2319] text-sm font-medium">Top utilisateurs — détections récurrentes</h3>
          </div>
          <div className="flex items-center gap-1 bg-[#FBF9F3] rounded-full p-0.5">
            <button
              onClick={() => setTab('client')}
              data-testid="pii-tab-client"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition ${
                tab === 'client' ? 'bg-[#D4AF37] text-[#0A0A0B] font-semibold' : 'text-[#4B5563] hover:text-[#2A2319]'
              }`}
            >
              <Users className="w-3 h-3" /> Clients
            </button>
            <button
              onClick={() => setTab('coach')}
              data-testid="pii-tab-coach"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs transition ${
                tab === 'coach' ? 'bg-[#D4AF37] text-[#0A0A0B] font-semibold' : 'text-[#4B5563] hover:text-[#2A2319]'
              }`}
            >
              <UserCog className="w-3 h-3" /> Coachs
            </button>
          </div>
        </div>

        {currentList.length === 0 ? (
          <div className="px-4 py-8 text-center text-[#6B7280] text-sm" data-testid="pii-offenders-empty">
            <ShieldCheck className="w-6 h-6 mx-auto mb-2 text-emerald-500" />
            Aucun utilisateur à signaler — conformité intacte.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {currentList.map((o, i) => (
              <div
                key={`${o.user_id}-${i}`}
                data-testid={`pii-offender-${i}`}
                className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-[#FAF7EE] transition"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#2A2319] text-sm font-medium truncate">{o.display_name}</span>
                    <LevelBadge level={o.max_level} />
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-[#6B7280] flex-wrap">
                    <span className="tabular-nums">{o.events} détection{o.events > 1 ? 's' : ''}</span>
                    {o.types?.length > 0 && (
                      <span className="text-[#4B5563]">{o.types.join(' · ')}</span>
                    )}
                    {o.last_seen && (
                      <span>dernière : {new Date(o.last_seen).toLocaleDateString('fr-FR')}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transparence zéro coût */}
      <div className="bg-white border border-[#F1ECDC] rounded-xl p-4 text-[11px] text-[#6B7280] leading-relaxed">
        <strong className="text-[#4B5563]">Fonctionnement</strong> — Détection locale via Web Speech API (Chrome / Edge / Safari),
        aucun appel API payant. Une passe post-session redacte la transcription avant stockage, garantissant
        que <strong className="text-emerald-400">zéro PII brute</strong> n'est persistée (ni en base, ni en logs, ni dans les alertes).
      </div>
    </div>
  );
}
